import React from 'react';
import { DreamTopic, findDreamBySlug, getDreamPageSources, formatSourcesLabel, dreamHasCases } from '../data/dreamsData';
import { playStoreUrl } from '../lib/appLinks';

interface DreamDetailViewProps {
  dream: DreamTopic;
  onSelectDream: (slug: string) => void;
  onSelectCategory: (categorySlug: string) => void;
  onSelectLetter: (letter: string) => void;
  onNavigateToDictionary: () => void;
  onStartAiInterpretation: (symbolName?: string) => void;
  onBackToHome: () => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const DreamDetailView: React.FC<DreamDetailViewProps> = ({
  dream,
  onSelectDream,
  onSelectCategory,
  onSelectLetter,
  onNavigateToDictionary,
  onStartAiInterpretation,
  onBackToHome
}) => {
  const sources = getDreamPageSources(dream);
  const hasSpecificSource = sources.hasIbnSirin || sources.hasNabulsi || sources.hasIbnShahin;
  const sourcesLabel = formatSourcesLabel(sources);

  const showIbnSirinSection = !dream.isDictionary && !!dream.ibnSirinInterpretation;
  const showNabulsiSection = !!dream.nabulsiInterpretation;
  const showCasesSection = dreamHasCases(dream);
  const showKeyVariations = dream.keyVariations && dream.keyVariations.length > 0;
  const showComparisonTable = dream.comparisonTable && dream.comparisonTable.length > 0;
  const showScholarlyNuances = !!dream.scholarlyNuances;
  const showPracticalAdvice = dream.practicalAdvice && dream.practicalAdvice.length > 0;
  const showCommonMistakes = dream.commonMistakes && dream.commonMistakes.length > 0;
  const showFaqs = dream.faqs && dream.faqs.length > 0;
  const showQuickAnswer = !!dream.quickAnswer;

  const isDictionaryEntry = !!dream.isDictionary;

  const EditorialBadge = () => (
    <span className="px-2 py-0.5 bg-teal-500/15 text-teal-300 text-[11px] font-bold rounded-md border border-teal-500/30 whitespace-nowrap">
      صياغة تحريرية
    </span>
  );

  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-slate-100 py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <article className="max-w-4xl mx-auto">
        {/* Breadcrumb navigation */}
        <nav aria-label="مسار التصفح" className="flex flex-wrap items-center gap-2 text-sm text-slate-300 mb-6 pb-4 border-b border-slate-700/60">
          <a href="/" onClick={(e) => preventDefault(e, onBackToHome)} className="hover:text-emerald-400 transition-colors no-underline">الرئيسية</a>
          <span>/</span>
          <a href="/dictionary" onClick={(e) => preventDefault(e, onNavigateToDictionary)} className="hover:text-emerald-400 transition-colors no-underline">قاموس الأحلام</a>
          <span>/</span>
          <a href={`/letter/${encodeURIComponent(dream.letter)}`} onClick={(e) => preventDefault(e, () => onSelectLetter(dream.letter))} className="hover:text-emerald-400 transition-colors no-underline">حرف ({dream.letter})</a>
          <span>/</span>
          <a href={`/category/${dream.categorySlug}`} onClick={(e) => preventDefault(e, () => onSelectCategory(dream.categorySlug))} className="hover:text-emerald-400 transition-colors no-underline">{dream.category}</a>
          <span>/</span>
          <span className="text-emerald-400 font-semibold">{dream.name}</span>
        </nav>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <a href={`/category/${dream.categorySlug}`} onClick={(e) => preventDefault(e, () => onSelectCategory(dream.categorySlug))} className="no-underline px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-semibold rounded-lg border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors inline-block">
              {dream.category}
            </a>
            <a href={`/letter/${encodeURIComponent(dream.letter)}`} onClick={(e) => preventDefault(e, () => onSelectLetter(dream.letter))} className="no-underline px-3 py-1 bg-slate-800 text-slate-300 text-xs font-semibold rounded-lg border border-slate-700 hover:bg-slate-700 transition-colors inline-block">
              حرف ({dream.letter})
            </a>
            {isDictionaryEntry && (
              <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-semibold rounded-lg border border-amber-500/20">
                من قاموس الأحلام
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
            {dream.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-300">
            <span>تاريخ النشر: <time dateTime={dream.datePublished}>{dream.datePublished}</time></span>
            <span>•</span>
            <span>آخر تحديث: <time dateTime={dream.dateModified}>{dream.dateModified}</time></span>
            <span>•</span>
            <span className="text-emerald-400 font-medium">المصادر المتاحة لهذا الرمز: {sourcesLabel}</span>
          </div>
        </header>

        {/* Summary / خلاصة التفسير */}
        {isDictionaryEntry ? (
          <div className="bg-gradient-to-r from-teal-950/40 via-slate-900 to-slate-900 border-r-4 border-teal-500 p-6 rounded-2xl mb-8 shadow-xl">
            <div className="flex items-center gap-2 text-teal-400 font-bold text-lg mb-2">
              <span>📖</span>
              <h2>التأصيل التراثي لرؤية {dream.name}</h2>
            </div>
            <p className="text-slate-200 text-base sm:text-lg leading-relaxed whitespace-pre-line">
              {dream.summary}
            </p>
            {!hasSpecificSource && (
              <div className="mt-4 pt-4 border-t border-slate-700/40 text-xs text-slate-400 leading-relaxed">
                💡 هذا التفسير مستند إلى مادة تراثية من معجم تفسير الأحلام والرؤى. المصدر: كتب التراث في علم التعبير.
              </div>
            )}
          </div>
        ) : (
          <div className="bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-900 border-r-4 border-emerald-500 p-6 rounded-2xl mb-8 shadow-xl">
            <div className="flex items-center gap-2 text-emerald-400 font-bold text-lg mb-2">
              <span>📌</span>
              <h2>خلاصة دلالة {dream.name} في المنام</h2>
            </div>
            <p className="text-slate-200 text-base sm:text-lg leading-relaxed">
              {dream.summary}
            </p>
          </div>
        )}

        {/* Quick Answer Callout (لجميع الصفحات عند توفرها) */}
        {showQuickAnswer && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 border-r-4 border-emerald-400 p-5 rounded-2xl mb-8">
            <div className="flex items-center gap-2 text-emerald-400 font-bold text-base mb-1.5">
              <span>⚡</span>
              <h3>الإجابة المباشرة وخلاصة الدلالة</h3>
              {isDictionaryEntry && <EditorialBadge />}
            </div>
            <p className="text-slate-100 text-sm sm:text-base leading-relaxed">
              {dream.quickAnswer}
            </p>
          </div>
        )}

        {/* App CTA سياقي - بعد الإجابة المباشرة مباشرة */}
        <div className="bg-slate-800/90 border border-emerald-500/40 rounded-2xl p-6 mb-8 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="text-right">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <span>هل كان "{dream.name}" جزءاً من حلم كامل؟</span>
              <span>🌙</span>
            </h3>
            <p className="text-slate-300 text-sm mt-1 leading-relaxed">
              اكتب رؤياك بالتفصيل بكل رموزها داخل التطبيق ليربط المفسر الذكي جميع عناصر الحلم بظروفك ويعطيك تأويلاً متكاملاً للحلم ككل، لا دلالة الرمز وحده.
            </p>
          </div>
          <a
            href={playStoreUrl('dream_symbol')}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap px-6 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 transition-all active:scale-95 no-underline flex items-center gap-2"
          >
            <span>📱 اكتب حلمك كاملًا في التطبيق</span>
            <span>←</span>
          </a>
        </div>

        {/* Editorial Meaning - صياغة تحريرية لصفحات القاموس */}
        {isDictionaryEntry && dream.editorialMeaning && (
          <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-2 flex-wrap text-emerald-400 font-bold text-lg mb-3">
              <span>🎯</span>
              <h2>معنى {dream.name} في المنام وما يُرجى منه</h2>
              <EditorialBadge />
            </div>
            <p className="text-slate-300 leading-relaxed text-base whitespace-pre-line">
              {dream.editorialMeaning}
            </p>
            <div className="mt-4 pt-4 border-t border-slate-700/40 text-xs text-slate-400 leading-relaxed">
              ✍️ صياغة تحريرية حديثة لتنظيم وشرح المعنى الوارد في المادة التراثية، وليست نصًّا منقولًا عن أحد المفسرين.
            </div>
          </section>
        )}

        {/* AI Call to Action - قبل الأقسام للقاموس، بعدها للرموز الغنية */}
        {isDictionaryEntry && (
          <div className="bg-slate-800/90 border border-emerald-500/40 rounded-2xl p-6 mb-10 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="text-right">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>هل رأيت "{dream.name}" في منامك بالتفصيل؟</span>
                <span>🌙</span>
              </h3>
              <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                اكتب تفاصيل حلمك وسيقوم الذكاء الاصطناعي بربط الرموز بظروفك الخاصة وتقديم تفسير مركّب شامل.
              </p>
              <p className="text-amber-300/80 text-xs mt-2 leading-relaxed">
                ⚠️ تفسير الذكاء الاصطناعي هو اجتهاد آلي ولا يُنسب مباشرة لأحد المفسرين.
              </p>
            </div>
            <button
              onClick={() => onStartAiInterpretation(`رأيت في منامي ${dream.name} و`)}
              className="whitespace-nowrap px-6 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 transition-all active:scale-95 flex items-center gap-2"
            >
              <span>تفسير حلمي الآن</span>
              <span>←</span>
            </button>
          </div>
        )}

        {/* Comparison Table - فقط للرموز الغنية */}
        {showComparisonTable && !isDictionaryEntry && (
          <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8 mb-8 overflow-hidden">
            <h2 className="text-2xl font-bold text-emerald-400 mb-4 pb-3 border-b border-slate-700 flex items-center gap-2">
              <span>📊</span>
              <span>جدول مقارنة: حالات ودلالات رؤية {dream.name}</span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm border-collapse min-w-[550px]">
                <thead>
                  <tr className="bg-slate-900/80 border-b border-slate-700 text-slate-200">
                    <th className="p-3 font-bold text-emerald-400">الحالة / الهيئة</th>
                    <th className="p-3 font-bold text-teal-300">التأويل المحمود (بشارة)</th>
                    <th className="p-3 font-bold text-rose-300">التأويل المكروه (تحذير)</th>
                    <th className="p-3 font-bold text-slate-400">السياق والقرائن</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {dream.comparisonTable.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-transparent' : 'bg-slate-900/30'}>
                      <td className="p-3 font-semibold text-white">{row.aspect}</td>
                      <td className="p-3 text-emerald-300/90 leading-relaxed">{row.goodMeaning}</td>
                      <td className="p-3 text-rose-300/90 leading-relaxed">{row.warningMeaning}</td>
                      <td className="p-3 text-slate-400 text-xs leading-relaxed">{row.context}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Scholarly Nuances - فقط للرموز الغنية */}
        {showScholarlyNuances && !isDictionaryEntry && (
          <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8 mb-8">
            <h2 className="text-2xl font-bold text-emerald-400 mb-4 pb-3 border-b border-slate-700 flex items-center gap-2">
              <span>📖</span>
              <span>أصول وقواعد تأصيل الرؤيا عند أهل العلم</span>
            </h2>
            <div className="text-slate-300 text-base leading-relaxed whitespace-pre-line space-y-2">
              {dream.scholarlyNuances}
            </div>
          </section>
        )}

        {/* AI CTA للرموز الغنية */}
        {!isDictionaryEntry && (
          <div className="bg-slate-800/90 border border-emerald-500/40 rounded-2xl p-6 mb-10 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="text-right">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>هل رأيت "{dream.name}" في منامك؟</span>
                <span>🌙</span>
              </h3>
              <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                اكتب تفاصيل حلمك وسيقوم الذكاء الاصطناعي الإسلامي بربط الرموز بظروفك الخاصة فوراً.
              </p>
            </div>
            <button
              onClick={() => onStartAiInterpretation(`رأيت في منامي ${dream.name} و`)}
              className="whitespace-nowrap px-6 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 transition-all active:scale-95 flex items-center gap-2"
            >
              <span>تفسير حلمي الآن</span>
              <span>←</span>
            </button>
          </div>
        )}

        {/* Content Sections (للرموز الغنية فقط: ابن سيرين، النابلسي، الحالات، ...) */}
        <div className="space-y-8">
          {/* Ibn Sirin Section - يظهر فقط إذا كانت هناك مادة فعلية */}
          {showIbnSirinSection && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-emerald-400 mb-4 pb-3 border-b border-slate-700 flex items-center gap-2">
                <span>تفسير {dream.name} للإمام ابن سيرين</span>
              </h2>
              <div className="text-slate-300 leading-relaxed text-base space-y-4">
                <p>{dream.ibnSirinInterpretation}</p>
              </div>
            </section>
          )}

          {/* Al Nabulsi Section - يظهر فقط إذا كانت هناك مادة فعلية منه */}
          {showNabulsiSection && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-emerald-400 mb-4 pb-3 border-b border-slate-700 flex items-center gap-2">
                <span>تفسير {dream.name} للإمام عبد الغني النابلسي</span>
              </h2>
              <div className="text-slate-300 leading-relaxed text-base space-y-4">
                <p>{dream.nabulsiInterpretation}</p>
              </div>
            </section>
          )}

          {/* Social Cases Breakdown - للرموز الغنية فقط */}
          {showCasesSection && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-emerald-400 mb-6 pb-3 border-b border-slate-700">
              دلالة {dream.name} بحسب الحالة الاجتماعية للرائي
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800">
                <h3 className="font-bold text-emerald-300 text-base mb-2">🌸 تفسير حلم {dream.name} للعزباء</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{dream.cases.single}</p>
              </div>
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800">
                <h3 className="font-bold text-emerald-300 text-base mb-2">💍 تفسير حلم {dream.name} للمتزوجة</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{dream.cases.married}</p>
              </div>
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800">
                <h3 className="font-bold text-emerald-300 text-base mb-2">🤰 تفسير حلم {dream.name} للحامل</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{dream.cases.pregnant}</p>
              </div>
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800">
                <h3 className="font-bold text-emerald-300 text-base mb-2">🕊️ تفسير حلم {dream.name} للمطلقة</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{dream.cases.divorced}</p>
              </div>
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800 md:col-span-2">
                <h3 className="font-bold text-emerald-300 text-base mb-2">🧔 تفسير حلم {dream.name} للرجل</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{dream.cases.man}</p>
              </div>
            </div>
            </section>
          )}

          {/* Key Variations - للرموز الغنية وللصفحات التحريرية */}
          {showKeyVariations && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-emerald-400 mb-6 pb-3 border-b border-slate-700 flex items-center gap-2 flex-wrap">
                <span>أهم الحالات والأشكال المختلفة لرؤية {dream.name}</span>
                {isDictionaryEntry && <EditorialBadge />}
              </h2>
              <div className="space-y-4">
                {dream.keyVariations.map((item, idx) => (
                  <div key={idx} className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                    <h3 className="font-bold text-white text-base mb-1 text-teal-300">• {item.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Practical Sunnah Advice - للرموز الغنية فقط */}
          {showPracticalAdvice && !isDictionaryEntry && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-emerald-400 mb-6 pb-3 border-b border-slate-700 flex items-center gap-2">
                <span>🤲</span>
                <span>الآداب النبوية والتوجيهات العملية للرائي</span>
              </h2>
              <div className="space-y-4">
                {dream.practicalAdvice.map((item, idx) => (
                  <div key={idx} className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                    <h3 className="font-bold text-emerald-300 text-base mb-1">✓ {item.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{item.advice}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Common Mistakes & Warnings - للرموز الغنية فقط */}
          {showCommonMistakes && !isDictionaryEntry && (
            <section className="bg-slate-800/40 border border-rose-900/30 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-rose-300 mb-6 pb-3 border-b border-slate-700 flex items-center gap-2">
                <span>⚠️</span>
                <span>أخطاء شائعة وتحذيرات في تأويل هذا الحلم</span>
              </h2>
              <ul className="list-disc list-inside space-y-2.5 text-slate-300 text-sm leading-relaxed pr-2">
                {dream.commonMistakes.map((mistake, idx) => (
                  <li key={idx} className="text-slate-200">
                    {mistake}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* FAQs Accordion - للرموز الغنية وللصفحات التحريرية */}
          {showFaqs && (
            <section className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-emerald-400 mb-6 pb-3 border-b border-slate-700 flex items-center gap-2 flex-wrap">
                <span>الأسئلة الشائعة حول رؤية {dream.name} في المنام</span>
                {isDictionaryEntry && <EditorialBadge />}
              </h2>
              <div className="space-y-4">
                {dream.faqs.map((faq, idx) => (
                  <details key={idx} className="bg-slate-900/60 p-4 rounded-xl border border-slate-800 group" open={idx === 0}>
                    <summary className="font-bold text-white cursor-pointer hover:text-emerald-400 transition-colors flex items-center justify-between">
                      <span>{faq.question}</span>
                      <span className="text-emerald-400 text-xs transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <p className="mt-3 text-slate-300 text-sm leading-relaxed border-t border-slate-800/80 pt-3">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Related Internal Links + التصنيف + الحرف (لجميع الصفحات) */}
          <section className="pt-6 border-t border-slate-700/60 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href={`/category/${dream.categorySlug}`}
                onClick={(e) => preventDefault(e, () => onSelectCategory(dream.categorySlug))}
                className="no-underline p-4 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 text-right transition-all group block"
              >
                <div className="text-xs text-slate-400 mb-1">التصنيف</div>
                <div className="font-bold text-white group-hover:text-emerald-400 transition-colors">
                  تصفح جميع رموز: {dream.category} ←
                </div>
              </a>
              <a
                href={`/letter/${encodeURIComponent(dream.letter)}`}
                onClick={(e) => preventDefault(e, () => onSelectLetter(dream.letter))}
                className="no-underline p-4 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 text-right transition-all group block"
              >
                <div className="text-xs text-slate-400 mb-1">فهرس الحرف</div>
                <div className="font-bold text-white group-hover:text-emerald-400 transition-colors">
                  كل الرموز التي تبدأ بحرف ({dream.letter}) ←
                </div>
              </a>
            </div>

            <div>
              <h2 className="text-xl font-bold text-white mb-4">تفسيرات وأحلام ذات صلة</h2>
              <div className="flex flex-wrap gap-2.5">
                {dream.relatedSlugs.map((rSlug) => {
                  const rel = findDreamBySlug(rSlug);
                  if (!rel) return null;
                  return (
                    <a
                      key={rel.slug}
                      href={`/dream/${rel.slug}`}
                      onClick={(e) => preventDefault(e, () => onSelectDream(rel.slug))}
                      className="no-underline px-4 py-2 bg-slate-800 hover:bg-emerald-600 hover:text-white text-slate-200 rounded-xl border border-slate-700 text-sm font-medium transition-all inline-block"
                    >
                      تفسير حلم {rel.name}
                    </a>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        {/* CTA قصير بنهاية الصفحة */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-center">
          <p className="text-sm text-slate-300">هل تريد تفسير الحلم كاملاً؟</p>
          <a
            href={playStoreUrl('dream_bottom')}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline inline-block px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all text-sm shadow-lg shadow-emerald-950/40"
          >
            📱 حمّل التطبيق ليفسّر حلمك كاملًا
          </a>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 pt-6 border-t border-slate-700/60 flex flex-wrap items-center justify-between gap-4">
          <a
            href="/dictionary"
            onClick={(e) => preventDefault(e, onNavigateToDictionary)}
            className="no-underline px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl border border-slate-700 transition-all text-sm inline-block"
          >
            ← العودة لقاموس الأحلام
          </a>
          <button
            onClick={() => onStartAiInterpretation(dream.name)}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all text-sm shadow-lg shadow-emerald-950/40"
          >
            تفسير حلم جديد بالذكاء الاصطناعي ✨
          </button>
        </div>
      </article>
    </div>
  );
};

export default DreamDetailView;
